import { motion } from 'framer-motion';
import {
    Briefcase,
    Check,
    ChevronDown,
    ChevronRight,
    MapPin,
    Search,
    Sliders,
    Star,
    Users
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const CandidatesList = () => {
  // States for filters and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    skills: [],
    experience: [],
    location: [],
    jobTitle: [],
    availability: [],
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [view, setView] = useState('grid');

  // Remote candidates data
  const [baseCandidates, setBaseCandidates] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter options
  const filterOptions = {
    skills: [
      'React',
      'Node.js',
      'TypeScript',
      'JavaScript',
      'AWS',
      'Docker',
      'PostgreSQL',
      'UI/UX',
      'Figma',
      'Design Systems',
    ],
    experience: ['0-2 years', '3-5 years', '6-8 years', '9+ years'],
    location: ['New York, NY', 'San Francisco, CA', 'Chicago, IL', 'Austin, TX', 'Remote'],
    jobTitle: [
      'Frontend Developer',
      'Backend Developer',
      'Full Stack Developer',
      'UX Designer',
      'DevOps Engineer',
      'Product Designer',
    ],
    availability: ['Immediately', '2 weeks', '1 month', '3 months'],
  };

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };

      if (updatedFilters[type].includes(value)) {
        updatedFilters[type] = updatedFilters[type].filter((item) => item !== value);
      } else {
        updatedFilters[type] = [...updatedFilters[type], value];
      }

      return updatedFilters;
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      skills: [],
      experience: [],
      location: [],
      jobTitle: [],
      availability: [],
    });
    setSearchQuery('');
  };

  // Fetch candidates when component mounts or when basic search parameters change
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);

        // Fetch all candidates once; client-side search/filters handle the rest
        const res = await api.get('/candidates', { params: { limit: 500 } });
        const remote = (res.data?.data?.candidates || []).map((u) => ({
          id: u._id,
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          jobTitle: u.currentJobTitle || '',
          location: `${u.address?.city || ''}${u.address?.state ? ', ' + u.address.state : ''}`.trim(),
          experience: u.totalExperience || 0,
          skills: (u.skills || []).map((s) => (typeof s === 'string' ? s : s.name)),
          matchScore: 0,
          appliedDate: '',
          status: 'New',
          availability: u.jobPreferences?.remoteWork ? 'Remote' : '',
          avatar: (u.firstName || 'U').charAt(0),
          email: u.email,
          phone: u.phone,
          workAuth: u.workAuthorization || u.workAuth || u.visaStatus || '',
        }));
        setBaseCandidates(remote);
      } catch (err) {
        console.error('Failed to fetch candidates', err);
        setBaseCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [searchQuery, filters.skills]);

  // Apply client-side filters/sorting/view to baseCandidates
  useEffect(() => {
    if (loading) return;

    let filteredCandidates = [...baseCandidates];

    // The remote API already handles keyword search, but we keep this as safeguard
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredCandidates = filteredCandidates.filter(
        (candidate) =>
          candidate.name?.toLowerCase().includes(query) ||
          candidate.jobTitle?.toLowerCase().includes(query) ||
          (candidate.skills || []).some((skill) => skill.toLowerCase().includes(query)) ||
          candidate.location?.toLowerCase().includes(query)
      );
    }

    // Apply filters
    Object.keys(filters).forEach((filterType) => {
      if (filters[filterType].length > 0) {
        filteredCandidates = filteredCandidates.filter((candidate) => {
          if (filterType === 'skills') {
            return filters[filterType].some((skill) =>
              candidate.skills.includes(skill)
            );
          } else if (filterType === 'experience') {
            return filters[filterType].some((range) => {
              const [min, max] = range.split('-');
              if (range === '9+ years') {
                return candidate.experience >= 9;
              } else {
                return (
                  candidate.experience >= parseInt(min) &&
                  candidate.experience <= parseInt(max)
                );
              }
            });
          } else if (filterType === 'location') {
            return filters[filterType].includes(candidate.location);
          } else if (filterType === 'jobTitle') {
            return filters[filterType].includes(candidate.jobTitle);
          } else if (filterType === 'availability') {
            return filters[filterType].includes(candidate.availability);
          }
          return true;
        });
      }
    });

    // Apply sorting
    switch (sortBy) {
      case 'name':
        filteredCandidates.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'experience':
        filteredCandidates.sort((a, b) => b.experience - a.experience);
        break;
      case 'applied':
        filteredCandidates.sort(
          (a, b) => new Date(b.appliedDate).getTime() - new Date(a.appliedDate).getTime()
        );
        break;
      case 'relevance':
      default:
        filteredCandidates.sort((a, b) => b.matchScore - a.matchScore);
        break;
    }

    setCandidates(filteredCandidates);
  }, [searchQuery, filters, sortBy, baseCandidates, loading]);

  // Get count of active filters
  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((count, filterGroup) => count + filterGroup.length, 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get status badge style
  const getStatusStyle = (status) => {
    switch (status) {
      case 'New':
        return 'bg-primary-100 text-primary-700';
      case 'Shortlisted':
        return 'bg-accent-100 text-accent-700';
      case 'Interviewed':
        return 'bg-warning-100 text-warning-700';
      case 'Offer':
        return 'bg-success-100 text-success-700';
      case 'Rejected':
        return 'bg-secondary-100 text-secondary-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get match score color
  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-success-600';
    if (score >= 80) return 'text-accent-600';
    if (score >= 70) return 'text-warning-600';
    return 'text-secondary-600';
  };

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page header */}
      <motion.div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        variants={itemVariants}
      >
        <div>
          <h1 className="page-title">Candidate Database</h1>
          <p className="text-gray-600 mt-1">
            Search, filter, and manage potential candidates
          </p>
        </div>
        
      </motion.div>

      {/* Search and filters */}
      <motion.div variants={itemVariants}>
        <div className="bg-white rounded-xl shadow-soft p-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search candidates by name, skills, job title or location"
                className="block w-full pl-10 pr-4 py-2.5 text-gray-900 bg-gray-50 border border-gray-200 rounded-lg font-body text-sm focus:outline-none focus:ring-1 focus:ring-primary-600 focus:border-primary-600"
              />
            </div>

            {/* View toggle and filter button */}
            <div className="flex items-center space-x-3">
              {/* View toggle */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setView('grid')}
                  className={`px-3 py-2 text-sm ${
                    view === 'grid'
                      ? 'bg-primary-50 text-primary-600'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setView('list')}
                  className={`px-3 py-2 text-sm ${
                    view === 'list'
                      ? 'bg-primary-50 text-primary-600'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  List
                </button>
              </div>

              {/* Filter button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline flex items-center"
              >
                <Sliders size={16} className="mr-2" />
                Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-primary-100 text-primary-700 text-xs rounded-full px-2 py-0.5">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {/* Sort dropdown */}
              <div className="relative">
                <div className="flex items-center space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="select-field py-2.5 pl-4 pr-10 appearance-none bg-white"
                  >
                    <option value="relevance">Sort by: Relevance</option>
                    <option value="name">Sort by: Name</option>
                    <option value="experience">Sort by: Experience</option>
                    <option value="applied">Sort by: Applied Date</option>
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronDown size={16} className="text-gray-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters section */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="font-accent font-medium text-sm text-gray-700">Filter by:</span>
                {getActiveFilterCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Skills filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Skills
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.skills.map((skill) => (
                      <button
                        key={skill}
                        onClick={() => handleFilterChange('skills', skill)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                          filters.skills.includes(skill)
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {skill}
                        {filters.skills.includes(skill) && (
                          <span className="ml-1">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Experience filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Experience
                  </p>
                  <div className="space-y-2">
                    {filterOptions.experience.map((exp) => (
                      <label
                        key={exp}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.experience.includes(exp)}
                          onChange={() => handleFilterChange('experience', exp)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.experience.includes(exp)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.experience.includes(exp) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{exp}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Combined filters section */}
                <div className="grid grid-cols-1 gap-6">
                  {/* Location filters */}
                  <div>
                    <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                      Location
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.location.map((location) => (
                        <button
                          key={location}
                          onClick={() => handleFilterChange('location', location)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                            filters.location.includes(location)
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {location}
                          {filters.location.includes(location) && (
                            <span className="ml-1">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Availability filters */}
                  <div>
                    <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                      Availability
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.availability.map((availability) => (
                        <button
                          key={availability}
                          onClick={() => handleFilterChange('availability', availability)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                            filters.availability.includes(availability)
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {availability}
                          {filters.availability.includes(availability) && (
                            <span className="ml-1">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Candidates list */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loading-spinner w-8 h-8 border-4"></div>
        </div>
      ) : candidates.length === 0 ? (
        <motion.div 
          className="text-center py-12"
          variants={itemVariants}
        >
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Users size={24} className="text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 font-heading">No candidates found</h2>
          <p className="mt-1 text-gray-500">
            Try adjusting your search or filters to find candidates.
          </p>
          <button
            onClick={clearFilters}
            className="mt-4 btn-secondary"
          >
            Clear Filters
          </button>
        </motion.div>
      ) : (
        view === 'grid' ? (
          // Grid view
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            variants={itemVariants}
          >
            {candidates.map((candidate) => (
              <motion.div
                key={candidate.id}
                className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden"
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                variants={itemVariants}
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-700 font-medium text-lg">{candidate.avatar}</span>
                    </div>
                    <div className="ml-3">
                      <h2 className="font-heading font-semibold text-lg text-gray-900">
                        {candidate.name}
                      </h2>
                      <p className="text-sm text-gray-600">{candidate.jobTitle}</p>
                    </div>
                  </div>

                  <div className="space-y-1 mb-4">
                    <div className="flex items-center text-sm">
                      <MapPin size={14} className="mr-2 text-gray-500" />
                      <span className="text-gray-700">{candidate.location}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Briefcase size={14} className="mr-2 text-gray-500" />
                      <span className="text-gray-700">{candidate.experience} years experience</span>
                    </div>
                    {candidate.workAuth && (
                      <div className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                        {candidate.workAuth}
                      </div>
                    )}
                  </div>

                  <div className="mb-4 flex flex-wrap gap-1.5">
                    {candidate.skills.slice(0, 3).map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {skill}
                      </span>
                    ))}
                    {candidate.skills.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                        +{candidate.skills.length - 3}
                      </span>
                    )}
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                    <Link
                      to={`/candidates/${candidate.id}`}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                    >
                      View Profile <ChevronRight size={16} className="ml-1" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          // List view
          <motion.div className="space-y-4" variants={itemVariants}>
            <div className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Experience
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Skills
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Match
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {candidates.map((candidate) => (
                      <tr key={candidate.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-primary-700 font-medium">
                                {candidate.avatar}
                              </span>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{candidate.name}</p>
                              <p className="text-xs text-gray-500">{candidate.jobTitle}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{candidate.location}</div>
                          {candidate.workAuth && (
                            <div className="text-xs mt-1 bg-gray-100 text-gray-800 inline-block px-2 py-0.5 rounded-full">
                              {candidate.workAuth}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{candidate.experience} years</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {candidate.skills.slice(0, 2).map((skill, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {skill}
                              </span>
                            ))}
                            {candidate.skills.length > 2 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                +{candidate.skills.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(
                              candidate.status
                            )}`}
                          >
                            {candidate.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <Star
                              size={14}
                              className={getMatchScoreColor(candidate.matchScore)}
                              fill="currentColor"
                            />
                            <span
                              className={`text-sm font-medium ${getMatchScoreColor(
                                candidate.matchScore
                              )}`}
                            >
                              {candidate.matchScore}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2 items-center justify-end">
                            <Link
                              to={`/candidates/${candidate.id}`}
                              className="text-primary-600 hover:text-primary-700"
                            >
                              View
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )
      )}
    </motion.div>
  );
};

export default CandidatesList; 